import { useCallback, useMemo, useRef } from "react";
import {
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type Path,
  get,
} from "react-hook-form";

import { type ValidationError } from "../types";
import { isValidationError } from "../utils";

type ErrorsTypes<TFieldValues extends FieldValues> =
  | FieldErrors<TFieldValues>
  | ValidationError
  | undefined;

/**
 * Merges multiple error objects and provides a method to retrieve the highest priority error message for a specific field.
 * The order of the error objects determines the priority of the errors.
 *
 * @template TFieldValues - The type of the field values.
 * @param {...ErrorsTypes<TFieldValues>[]} errors - The error objects to merge. The order of these objects determines the priority of the errors.
 *
 * @returns {object} An object containing a method to retrieve the highest priority error message for a specific field.
 * @returns {function} error - A function that takes a field path and returns the highest priority error message for that field, or `undefined` if no error exists.
 *
 * @example
 * const { error } = useMergeErrors(clientErrors, serverErrors);
 * const errorMessage = error('fieldName');
 *
 * @see https://github.com/react-hook-form/react-hook-form/blob/e2df9f4bb42b37296d7b153edfb0fa5958cfde88/src/logic/createFormControl.ts#L901
 */
export function useMergeErrors<TFieldValues extends FieldValues>(
  ...errors: ErrorsTypes<TFieldValues>[]
) {
  const ref = useRef(errors);
  useMemo(() => {
    ref.current = errors;
  }, [errors]);
  const getError = useCallback((field: Path<TFieldValues>) => {
    return ref.current
      .map((error) => {
        if (!error) {
          return [];
        }

        if (isValidationError(error)) {
          return error.errors[field];
        }

        return [(get(error, field) as FieldError | undefined)?.message];
      })
      .flat()
      .filter(Boolean) as string[];
  }, []);

  return {
    error: useCallback(
      (field: Path<TFieldValues>) => getError(field)?.[0],
      [getError],
    ),
  };
}
